package com.tovtalk.ui.screen.settings

import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import com.tovtalk.ui.scaffold.ScreenScaffold
import com.tovtalk.ui.scaffold.TovTalkTopBar
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import androidx.compose.ui.text.font.FontWeight
import com.tovtalk.di.koinInject
import com.tovtalk.domain.entity.User
import com.tovtalk.domain.entity.DomainResult
import com.tovtalk.domain.repository.UserRepository
import com.tovtalk.ui.components.CenteredContentWrapper
import com.tovtalk.ui.components.TovTalkAvatar
import com.tovtalk.ui.theme.TovTalkTheme
import com.tovtalk.utils.strings.StringProvider
import kotlinx.coroutines.launch

@Composable
fun MyFollowingScreen(
    onNavigateBack: () -> Unit,
    onNavigateToProfile: ((Int) -> Unit)? = null,
    userRepository: UserRepository = koinInject()
) {
    var users by remember { mutableStateOf<List<User>>(emptyList()) }
    var loading by remember { mutableStateOf(true) }
    var currentPage by remember { mutableStateOf(0) }
    var hasMore by remember { mutableStateOf(true) }
    var loadingMore by remember { mutableStateOf(false) }
    var currentUserId by remember { mutableStateOf<Int?>(null) }
    var unfollowLoadingId by remember { mutableStateOf<Int?>(null) }

    val scope = rememberCoroutineScope()
    val listState = rememberLazyListState()

    LaunchedEffect(Unit) {
        when (val r = userRepository.getCurrentUser()) {
            is DomainResult.Success -> {
                currentUserId = r.data.id
                loading = true
                when (val followR = userRepository.getFollowing(r.data.id, page = 0)) {
                    is DomainResult.Success -> {
                        users = followR.data
                        hasMore = followR.data.size >= 20
                        currentPage = 0
                    }
                    else -> {}
                }
            }
            else -> {}
        }
        loading = false
    }

    LaunchedEffect(listState.layoutInfo, users.size, hasMore, loadingMore) {
        if (currentUserId == null || users.isEmpty() || !hasMore || loadingMore) return@LaunchedEffect
        val layoutInfo = listState.layoutInfo
        val lastVisibleIndex = layoutInfo.visibleItemsInfo.lastOrNull()?.index ?: 0
        if (lastVisibleIndex >= users.size - 2) {
            loadingMore = true
            val nextPage = currentPage + 1
            when (val r = userRepository.getFollowing(currentUserId!!, page = nextPage)) {
                is DomainResult.Success -> {
                    users = users + r.data
                    hasMore = r.data.size >= 20
                    currentPage = nextPage
                }
                else -> hasMore = false
            }
            loadingMore = false
        }
    }

    fun unfollowUser(userId: Int) {
        scope.launch {
            unfollowLoadingId = userId
            when (userRepository.unfollowUser(userId)) {
                is DomainResult.Success -> users = users.filter { it.id != userId }
                else -> {}
            }
            unfollowLoadingId = null
        }
    }

    ScreenScaffold(
        topBar = {
            TovTalkTopBar(
                title = StringProvider.get("settings_my_following"),
                onBack = onNavigateBack,
            )
        },
        constrainContentWidth = false,
    ) { paddingValues ->
        val myFollowingContent = @Composable {
        if (loading) {
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .padding(paddingValues),
                contentAlignment = Alignment.Center
            ) {
                CircularProgressIndicator(color = TovTalkTheme.colors.brand)
            }
        } else if (currentUserId == null) {
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .padding(paddingValues),
                contentAlignment = Alignment.Center
            ) {
                Text(
                    text = StringProvider.get("settings_my_following_empty"),
                    style = TovTalkTheme.typography.bodyLarge,
                    color = TovTalkTheme.colors.textSecondary
                )
            }
        } else if (users.isEmpty()) {
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .padding(paddingValues),
                contentAlignment = Alignment.Center
            ) {
                Text(
                    text = StringProvider.get("settings_my_following_empty"),
                    style = TovTalkTheme.typography.bodyLarge,
                    color = TovTalkTheme.colors.textSecondary
                )
            }
        } else {
            LazyColumn(
                state = listState,
                modifier = Modifier
                    .fillMaxSize()
                    .padding(paddingValues),
                verticalArrangement = Arrangement.spacedBy(TovTalkTheme.spacing.xs)
            ) {
                items(users) { user ->
                    MyFollowingItem(
                        user = user,
                        onProfileClick = { onNavigateToProfile?.invoke(user.id) },
                        onUnfollow = { unfollowUser(user.id) },
                        isUnfollowLoading = unfollowLoadingId == user.id
                    )
                }
                if (loadingMore) {
                    item {
                        Box(
                            modifier = Modifier
                                .fillMaxWidth()
                                .padding(TovTalkTheme.spacing.md),
                            contentAlignment = Alignment.Center
                        ) {
                            CircularProgressIndicator(
                                modifier = Modifier.size(24.dp),
                                color = TovTalkTheme.colors.brand
                            )
                        }
                    }
                }
            }
        }
        }
        CenteredContentWrapper { myFollowingContent() }
    }
}

@Composable
private fun MyFollowingItem(
    user: User,
    onProfileClick: () -> Unit,
    onUnfollow: () -> Unit,
    isUnfollowLoading: Boolean
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clickable(onClick = onProfileClick)
            .padding(horizontal = TovTalkTheme.spacing.md, vertical = TovTalkTheme.spacing.sm),
        verticalAlignment = Alignment.CenterVertically
    ) {
        TovTalkAvatar(
            imageUrl = user.profilePhoto,
            name = user.fullname,
            size = 48.dp
        )
        Column(
            modifier = Modifier
                .weight(1f)
                .padding(horizontal = TovTalkTheme.spacing.md)
        ) {
            Text(
                text = user.fullname.ifBlank { user.username },
                style = TovTalkTheme.typography.bodyLarge,
                fontWeight = FontWeight.Medium,
                color = TovTalkTheme.colors.textPrimary
            )
            Text(
                text = "@${user.username}",
                style = TovTalkTheme.typography.bodySmall,
                color = TovTalkTheme.colors.textSecondary
            )
        }
        TextButton(
            onClick = onUnfollow,
            enabled = !isUnfollowLoading
        ) {
            if (isUnfollowLoading) {
                CircularProgressIndicator(
                    modifier = Modifier.size(20.dp),
                    color = TovTalkTheme.colors.brand,
                    strokeWidth = 2.dp
                )
            } else {
                Text(
                    text = StringProvider.get("settings_unfollow"),
                    color = TovTalkTheme.colors.textSecondary
                )
            }
        }
    }
}
